import * as React from 'react';

import AutoSuggestInput from '../../../../../../../common/AutoSuggestInput/AutoSuggestInput';
import Service, { Operators } from '../../../../../../../lib/App';
import InputControlProps from '../ConstraintValueInput/InputControlProps';
import LanguageInput from '../ConstraintValueInput/LanguageInput';
import {
  MultiSelectDropdown,
  SelectDropdown,
} from '../ConstraintValueInput/SelectDropdown';
import MultiSelectDropdownV2, {
  MultiSelectDropdownV2Props,
} from '../ConstraintValueInput/SelectDropdown/MultiSelectDropdownV2';
import SelectTimeInterval from '../ConstraintValueInput/SelectTimeInterval/SelectTimeInterval';
import {
  SelectTree,
  SelectTreeProps,
} from '../ConstraintValueInput/SelectTree';
import SelectTreeV2, {
  SelectTreeV2Props,
} from '../ConstraintValueInput/SelectTree/SelectTreeV2';
import { TypeInput } from '../ConstraintValueInput/TypeInput';
import { AppVersion } from '@/common/components';
import { GroupType } from '@/common/const';

// I figured that it would be better if we just define the dropdownlist here
// AutoSuggestDropDownList takes facet as input and translate it as a list of dropdowns
export const AutoSuggestDropDownList = ({
  multiple,
  placeholder = 'Select',
  facetName,
  ...props
}: InputControlProps) => (
  <AutoSuggestInput
    placeholder={placeholder}
    initialValue={props.value}
    availableValues={props.availableValues}
    style={{ minWidth: 200, width: '100%' }}
    addToChoiceInput={(value) => props.onChange(value)}
    api={Service.planner.fetchGroupsV2}
    multiple={multiple}
    tooltipEnabled={true}
    tooltipTitle="Start typing to get more suggestions."
    tooltipPlacement="right"
    facet={facetName}
    type={GroupType.STATIC}
    disabled={props.disabled}
    allowClear={true}
  />
);

export const MultiDropDownList = ({
  multiple,
  placeholder = 'Select',
  facetName,
  ...props
}: InputControlProps<string>) => (
  <MultiSelectDropdown {...{ placeholder, facetName, multiple, ...props }} />
);

export const DropDownList = ({
  multiple,
  placeholder = 'Select',
  facetName,
  ...props
}: InputControlProps) => (
  <SelectDropdown {...{ placeholder, facetName, multiple, ...props }} />
);

export const GetFacetInputComponent = (facet: string, op: string) => {
  //stricter match first
  if (facet in FacetInputMap && op in FacetInputMap[facet]) {
    return FacetInputMap[facet][op];
  }
  //custom facets only allow get typeinput
  if (
    !Service.cache
      .getAllFacets()
      .map((f) => f.name)
      .includes(facet)
  ) {
    return FacetInputMap['default']['typeinput'];
  }
  if (op === 'in' || op === 'not in') {
    return FacetInputMap['default']['autosuggest'];
  }
  return FacetInputMap['default']['typeinput'];
};

export const GetFacetOperators = (facet: string) => {
  if (!FacetInputMap[facet]) {
    return Object.keys(Operators);
  }
  return Object.keys(FacetInputMap[facet]);
};

const FacetInputMap: {
  [key: string]: { [op: string]: React.FC<InputControlProps> };
} = {
  default: {
    typeinput: (props: InputControlProps) => <TypeInput {...props} />,
    autosuggest: (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  az: {},
  ctry: {
    in: (props: MultiSelectDropdownV2Props) => (
      <MultiSelectDropdownV2 {...props} />
    ),
    'not in': (props: MultiSelectDropdownV2Props) => (
      <MultiSelectDropdownV2 {...props} />
    ),
  },
  city: {
    in: (props: SelectTreeV2Props) => <SelectTreeV2 {...props} />,
    'not in': (props: SelectTreeV2Props) => <SelectTreeV2 {...props} />,
  },
  svc: {
    in: (props: SelectTreeProps) => <SelectTree {...props} />,
    'not in': (props: SelectTreeProps) => <SelectTree {...props} />,
  },
  area: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  // Device type will be put manually
  dvc: {
    '=': (props: InputControlProps) => <AppVersion {...props} />,
    '<=': (props: InputControlProps) => <AppVersion {...props} />,
    '>=': (props: InputControlProps) => <AppVersion {...props} />,
  },
  dlc: {
    in: (props: InputControlProps) => <MultiDropDownList {...props} />,
    'not in': (props: InputControlProps) => <MultiDropDownList {...props} />,
  },
  pax: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  dax: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  tex: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  mex: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  meu: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  mes: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  bkg: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  osv: {
    '=': (props: InputControlProps) => <TypeInput {...props} />,
    '<=': (props: InputControlProps) => <TypeInput {...props} />,
    '>=': (props: InputControlProps) => <TypeInput {...props} />,
    '>': (props: InputControlProps) => <TypeInput {...props} />,
    '<': (props: InputControlProps) => <TypeInput {...props} />,
  },
  // dom: {
  //   'in': (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
  //   'not in': (props: InputControlProps) => <AutoSuggestDropDownList {...props} />
  // },
  loc: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
    'in area': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...transformProps(props)} disabled />
    ),
  },
  lgh: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  sid: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  req: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  tag: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  seg: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  // err: {
  //   in: (props: InputControlProps) => <TypeInput {...props} />,
  //   'not in': (props: InputControlProps) => <TypeInput {...props} />
  // },
  bch: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  ser: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  host: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  ip: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  tkn: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  src: {
    '=': (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    '<>': (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
  },
  dla: {
    in: (props: InputControlProps) => <LanguageInput {...props} />,
    'not in': (props: InputControlProps) => <LanguageInput {...props} />,
  },
  pre: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  ts: {
    in: (props: InputControlProps) => <SelectTimeInterval {...props} />,
    'not in': (props: InputControlProps) => <SelectTimeInterval {...props} />,
  },
  dvn: {
    '=': (props: InputControlProps) => <TypeInput {...props} />,
    '<>': (props: InputControlProps) => <TypeInput {...props} />,
    contains: (props: InputControlProps) => <TypeInput {...props} />,
    'not contains': (props: InputControlProps) => <TypeInput {...props} />,
  },
  patid: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
    '=': (props: InputControlProps) => <TypeInput {...props} />,
  },
  stage: {
    in: (props: InputControlProps) => <MultiDropDownList {...props} />,
    'not in': (props: InputControlProps) => <MultiDropDownList {...props} />,
  },
  dsf: {
    in: (props: InputControlProps) => <MultiDropDownList {...props} />,
    'not in': (props: InputControlProps) => <MultiDropDownList {...props} />,
  },
  sui: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  grp: {
    in: (props: InputControlProps) => <AutoSuggestDropDownList {...props} />,
    'not in': (props: InputControlProps) => (
      <AutoSuggestDropDownList {...props} />
    ),
  },
  ost: {
    '=': (props: InputControlProps) => <DropDownList {...props} />,
    '<>': (props: InputControlProps) => <DropDownList {...props} />,
  },
  fbt: {
    '=': (props: InputControlProps) => <DropDownList {...props} />,
    '<>': (props: InputControlProps) => <DropDownList {...props} />,
  },
};

export function transformProps(props: InputControlProps) {
  const newProps = Object.assign({}, props);
  if (['in area', 'not in area'].includes(newProps.operator)) {
    newProps.facetName = 'area';
    newProps.disabled = true;
  }

  return newProps;
}

export default FacetInputMap;
